import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';

import { TableCell, TableRow, Stack, Box, Grid, Button } from '@mui/material';

import { AdminAxios as Axios } from '../../Assets/Axios/Axios';

import { PROFESSIONAL_STATUS } from '../../Constant/Enums';

import { useToastContext } from '../../Context/ToastContext';

import { toCapitalize } from '../../Helpers';

import { RejectProfessionalRequest, UpdateProfessionalStatus } from '../../Server/Mutation/index';
import { FindProfessional, RequestedProfessionalsQueryAdmin } from '../../Server/Query';

import SearchField from './Components/SearchField';
import CustomTable from './Components/CustomTable';
import ProfessionalDetails from './Components/ProfessionalDetails';

const ProfessionalsRequest = () => {
  const { showToastMessage } = useToastContext();

  const [requestedProfessionals, setRequestedProfessionals] = useState([]);
  const [search, setSearch] = useState('');
  const [isActionsDisabled, setIsActionsDisabled] = useState(true);
  const [selectedProfessionalDetails, setSelectedProfessionalDetails] = useState(
    initialProfessionalDetails
  );

  const selectedProfessionalUniqueID = useRef(null);

  const filteredProfessionals = useMemo(() => {
    if (search === '') return requestedProfessionals;

    return requestedProfessionals.filter(({ name, professionalId }) => {
      const isMatchedByName = name.toLowerCase().startsWith(search.toLowerCase());

      const isMatchedById = professionalId.toLowerCase().startsWith(search.toLowerCase());

      return isMatchedByName || isMatchedById;
    });
  }, [search, requestedProfessionals]);

  const fetchRequestedProfessionals = useCallback(() => {
    Axios(RequestedProfessionalsQueryAdmin)
      .then(({ data, errors }) => {
        if (errors) {
          showToastMessage("Something went wrong, We couldn't load the data.");
          return undefined;
        }

        const professionals = (data?.Professionals ?? []).map(parseProfessional);
        setRequestedProfessionals(professionals);
      })
      .catch(() => showToastMessage("Something went wrong, We couldn't load the data."));
  }, [showToastMessage]);

  useEffect(fetchRequestedProfessionals, [fetchRequestedProfessionals]);

  function reset() {
    setIsActionsDisabled(true);
    setSelectedProfessionalDetails(initialProfessionalDetails);
  }

  const handleClickTableRecord = useCallback(
    async professionalId => {
      setIsActionsDisabled(false);
      selectedProfessionalUniqueID.current = professionalId;

      const query = FindProfessional({ professionalUniqueID: professionalId });
      const { data, errors } = await Axios(query);

      if (errors) {
        showToastMessage('Something went wrong,Unable to load data');
        return undefined;
      }

      const professional = data.Professional;
      const service = professional.Service;

      setSelectedProfessionalDetails({
        name: getFullName(professional),
        avatar: professional.avatar,
        postCode: getField(professional, 'postalCode', 'postal_code'),
        address: getField(professional, 'addressLine1', 'address_line_1'),
        province: professional.province,
        phoneNumber: getField(professional, 'mobileNumber', 'mobile_number'),
        service: {
          name: toCapitalize(service?.name),
          location: service?.location,
          serviceLink: `${service?.website_url ?? ''}  ${service?.facebook_url ?? ''}`.trim(),
        },
      });
    },
    [showToastMessage]
  );

  async function handleClickAccept() {
    const response = await Axios(UpdateProfessionalStatus({
        professionalStatus: PROFESSIONAL_STATUS.ACCEPTED,
        professionalUniqueID: selectedProfessionalUniqueID.current,
      }));

    if (response?.errors) {
      showToastMessage('Something went wrong');
      return undefined;
    }

    showToastMessage('Updated Successfully ', 'success');
    fetchRequestedProfessionals();
    reset();
  }

  async function handleClickReject() {
    const response = await Axios(RejectProfessionalRequest({ id: selectedProfessionalUniqueID.current }));

    if (response?.errors) {
      showToastMessage('Something went wrong');
      return undefined;
    }

    showToastMessage('Updated Successfully ', 'success');
    fetchRequestedProfessionals();
    reset();
  }

  function handleChangeSearch(event) {
    setSearch(event.target.value);
  }

  const tableRecords = useMemo(
    () =>
      filteredProfessionals.map(row => (
        <TableRow key={row.id} hover onClick={() => handleClickTableRecord(row.id)}>
          <TableCell>{row.no}</TableCell>
          <TableCell>{row.professionalId}</TableCell>
          <TableCell>{row.name}</TableCell>
          <TableCell>{row.phoneNumber}</TableCell>
          <TableCell>{row?.service?.name}</TableCell>
          <TableCell>{row?.service?.location}</TableCell>
          <TableCell>{row.joinedDate}</TableCell>
        </TableRow>
      )),
    [filteredProfessionals, handleClickTableRecord]
  );

  return (
    <Box px={{ xs: 2.5, md: 5 }} my={{ xs: 2.5, md: 5 }}>
      <SearchField sx={{ mb: 3 }} value={search} onChange={handleChangeSearch} />

      <CustomTable
        px={4}
        mb={3}
        height='300px'
        tableHeadings={[
          'No.',
          'Professional ID',
          'Name',
          'Phone Number',
          'Work',
          'Service location ',
          'Date and time',
        ]}>
        {tableRecords}
      </CustomTable>

      <Grid container columns={{ xs: 1, md: 2 }}>
        <Grid item xs={1}>
          <ProfessionalDetails
            professionalDetails={selectedProfessionalDetails}
            p={{ xs: 2, md: 4 }}
          />
        </Grid>
        <Grid item xs={1} md={0.5} mx='auto' mt={{ xs: 2, md: 4 }}>
          <Stack direction='row' gap={{ xs: 1.5, md: 3 }}>
            <Button
              color='green4'
              fullWidth
              sx={{ color: 'white' }}
              disabled={isActionsDisabled}
              onClick={handleClickAccept}>
              Accept
            </Button>
            <Button
              color='red3'
              fullWidth
              sx={{ color: 'white' }}
              disabled={isActionsDisabled}
              onClick={handleClickReject}>
              Reject
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

const initialProfessionalDetails = {
  name: null,
  avatar: null,
  address: null,
  province: null,
  postCode: null,
  service: {
    name: null,
    location: null,
    serviceLink: null,
  },
  phoneNumber: null,
  profileLink: null,
};

function parseProfessional(professional, index) {
  const dateFormatter = Intl.DateTimeFormat('default', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });

  const no = (index + 1).toString().padStart(2, '0');

  const data = {
    no,
    id: professional.id,
    professionalId: getField(professional, 'professionalId', 'professional_id'),
    name: getFullName(professional),
    phoneNumber: getField(professional, 'mobileNumber', 'mobile_number'),
    joinedDate: dateFormatter.format(new Date(getField(professional, 'createdAt', 'created_at'))),
  };

  if (professional.Service)
    data.service = {
      name: toCapitalize(professional.Service.name),
      location: professional.Service.location,
    };

  return data;
}

function getField(source, camelKey, snakeKey) {
  return source?.[camelKey] ?? source?.[snakeKey] ?? '';
}

function getFullName(person) {
  return [getField(person, 'firstName', 'first_name'), getField(person, 'lastName', 'last_name')]
    .filter(Boolean)
    .join(' ');
}

export default ProfessionalsRequest;
